// client/src/hooks/useFirstPartyAnalytics.ts
/**
 * lamirage.co SPA hook (spec §1.1): a pageview on every wouter location change, preceded by an
 * engagement beacon for the path being LEFT (an SPA route change never fires pagehide).
 * Mounted in AppContent beside usePageTracking(), before every host branch, so wedding sites,
 * live pages, public pages and the portal all report.
 * ⚠️ useLAYOUTEffect, deliberately: React runs a child's passive effects BEFORE its parent's, so with
 *    useEffect NotFound's markNotFound() (and any child's first-load trackEvent) would run before this
 *    pageview — flagging the page being LEFT as the 404 and dropping events while the tracker is off.
 *    Every layout effect of a commit runs before any passive effect, so the pageview goes out first.
 * Named useFirstPartyAnalytics (not useAnalytics) because client/src/hooks/useAnalytics.ts is the older, unrelated
 * analyticsService hook (still imported by components/Analytics/*).
 * Not in the native app (client-app, Capacitor): isAppBuild() is the house flag; the tracker also
 * refuses https://localhost and capacitor:// on its own.
 */
import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { flushEngagement, initAnalytics, trackPageview } from '@shared/analytics/tracker';
import { getToken, isAppBuild } from '@/lib/net';

export function useFirstPartyAnalytics(): void {
  const [location] = useLocation();
  const prev = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || isAppBuild() || !/^https?:$/.test(window.location.protocol)) return;
    initAnalytics({ app: 'lamirage-co', getToken });
    if (prev.current === location) return;
    if (prev.current !== null) flushEngagement();
    trackPageview(window.location.pathname);
    prev.current = location;
  }, [location]);
}
